import { VStack, HStack, Spinner, Text, Badge, Link, Box, Dialog, CloseButton } from '@chakra-ui/react';
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import { ResponsiveModal } from '../shared/ResponsiveModal'
import { ResponsiveTable, type ResponsiveColumn } from '../shared/ResponsiveTable'
import { useUnionFootprintDetail } from '../../hooks/useGeoAnalytics'
import type { WorksiteRow } from '../../types/geoAnalytics'

interface Props {
  geoUnitId: string
  isOpen: boolean
  onClose: () => void
}

export function UnionFootprintDetailModal({ geoUnitId, isOpen, onClose }: Props) {
  const navigate = useNavigate()
  const { data, isLoading, error } = useUnionFootprintDetail(geoUnitId, { enabled: isOpen })

  const columns: ResponsiveColumn<WorksiteRow>[] = [
    {
      header: 'Worksite',
      mobileRole: 'title',
      cell: (w) => <Text fontWeight="medium">{w.label}</Text>,
    },
    {
      header: 'Union',
      mobileRole: 'badge',
      cell: (w) =>
        w.unions.length > 0 ? (
          <HStack gap={1} flexWrap="wrap">
            {w.unions.map((u, i) => (
              <Badge key={`${u}-${i}`} bg="info.subtle" color="info.fg">{u}</Badge>
            ))}
          </HStack>
        ) : (
          <Badge colorPalette="gray">Unaffiliated</Badge>
        ),
    },
    {
      header: 'Steward',
      mobileRole: 'badge',
      cell: (w) =>
        w.hasSteward ? <Badge colorPalette="green">✓ Steward</Badge> : <Badge colorPalette="gray">No steward</Badge>,
    },
    {
      header: 'Type',
      mobileRole: 'meta',
      cell: (w) => <Text fontSize="sm" color="gray.600">{w.type ?? '—'}</Text>,
    },
    {
      header: 'City',
      mobileRole: 'meta',
      cell: (w) => <Text fontSize="sm" color="gray.600">{w.city ?? '—'}</Text>,
    },
  ]

  const isEmpty =
    !!data && data.employers.length === 0 && data.independentWorksites.length === 0

  return (
    <ResponsiveModal isOpen={isOpen} onClose={onClose} size="4xl" scrollBehavior="inside">
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content>
          <Dialog.Header>Union footprint — worksites in this district</Dialog.Header>
          <Dialog.CloseTrigger asChild><CloseButton size="sm" /></Dialog.CloseTrigger>
          <Dialog.Body pb={6}>
            {isLoading && (
              <VStack py={12}>
                <Spinner size="lg" color="accent" />
              </VStack>
            )}

            {!!error && (
              <Text color="red.500">Couldn't load worksites. Please try again.</Text>
            )}

            {!isLoading && !error && isEmpty && (
              <Text color="gray.500">No worksites in this district.</Text>
            )}

            {!isLoading && !error && data && !isEmpty && (
              <VStack align="stretch" gap={6}>
                {data.employers.map((emp) => (
                  <Box key={emp.businessId}>
                    <HStack justify="space-between" mb={2}>
                      <Link fontWeight="semibold" color="accent" asChild><RouterLink to={`/dashboard/businesses/${emp.businessId}/detail`}>
                          {emp.businessName}
                        </RouterLink></Link>
                      <Text fontSize="xs" color="gray.500">
                        {emp.worksites.length} worksite{emp.worksites.length === 1 ? '' : 's'}
                      </Text>
                    </HStack>
                    <ResponsiveTable
                      columns={columns}
                      rows={emp.worksites}
                      keyFn={(w) => w.worksiteId}
                      onRowClick={(w) => navigate(`/dashboard/worksites/${w.worksiteId}`)}
                      bare
                    />
                  </Box>
                ))}

                {data.independentWorksites.length > 0 && (
                  <Box>
                    <Text fontWeight="semibold" mb={2}>Independent worksites</Text>
                    <ResponsiveTable
                      columns={columns}
                      rows={data.independentWorksites}
                      keyFn={(w) => w.worksiteId}
                      onRowClick={(w) => navigate(`/dashboard/worksites/${w.worksiteId}`)}
                      bare
                    />
                  </Box>
                )}
              </VStack>
            )}
          </Dialog.Body>
        </Dialog.Content>
      </Dialog.Positioner>
    </ResponsiveModal>
  );
}
